// src/components/Navigation.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, Code2, Menu, X, History, LogOut, User, Clock, Zap, DollarSign } from 'lucide-react';
import { useRef } from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useAuth } from '../hooks/useAuth';
import './Navigation.css';

const Navigation = ({ onShowHistory, user, historyOpen }) => {
  const { logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [sessionStats, setSessionStats] = useState({ sessionTokensUsed: 0, totalTokens: 0, totalCost: 0, sessionTokensRemaining: 10000, balance: 0 });
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    const loadSessionStats = async () => {
      try {
        // Get real-time session data from server
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/user/profile`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setSessionStats({
            sessionTokensUsed: userData.sessionTokensUsed || 0,
            totalTokens: userData.totalTokensUsed || 0,
            totalCost: userData.totalCost || 0,
            sessionTokensRemaining: Math.max(0, (userData.maxTokens || 10000) - (userData.sessionTokensUsed || 0)),
            balance: parseFloat(userData.balance) || 0
          });
        } else {
          // Fallback to localStorage
          const stats = JSON.parse(localStorage.getItem('sessionStats') || '{}');
          setSessionStats({
            sessionTokensUsed: stats.tokensUsed || 0,
            totalTokens: stats.totalTokens || 0,
            totalCost: stats.totalCost || 0,
            sessionTokensRemaining: Math.max(0, 10000 - (stats.tokensUsed || 0)),
            balance: parseFloat(user?.balance) || 0
          });
        }
      } catch (error) {
        console.error('Failed to load session stats:', error);
        // Fallback to localStorage
        const stats = JSON.parse(localStorage.getItem('sessionStats') || '{}');
        setSessionStats({
          sessionTokensUsed: stats.tokensUsed || 0,
          totalTokens: stats.totalTokens || 0,
          totalCost: stats.totalCost || 0,
          sessionTokensRemaining: Math.max(0, 10000 - (stats.tokensUsed || 0)),
          balance: parseFloat(user?.balance) || 0
        });
      }
    };
    
    loadSessionStats();
    
    const handleStatsUpdate = () => loadSessionStats();
    window.addEventListener('sessionStatsUpdated', handleStatsUpdate);
    window.addEventListener('userStatsUpdated', handleStatsUpdate);
    
    // Refresh every 10 seconds to get latest data
    const interval = setInterval(loadSessionStats, 10000);
    
    return () => {
      window.removeEventListener('sessionStatsUpdated', handleStatsUpdate);
      window.removeEventListener('userStatsUpdated', handleStatsUpdate);
      clearInterval(interval);
    };
  }, []);

  const isActive = (path) => {
    if (path === '/' || path === '/chat') {
      return location.pathname === '/' || location.pathname === '/chat';
    }
    return location.pathname === path;
  };

  const handleNavClick = (path) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && menuRef.current && !menuRef.current.contains(event.target)) {
        // Don't close if clicking on menu toggle button
        if (!event.target.closest('.menu-toggle')) {
          setIsMenuOpen(false);
        }
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen]);

  if (isMobile) {
    return (
      <nav className="mobile-nav">
        <div className="mobile-nav-header">
          <div className="brand">
            <MessageSquare size={24} />
            <span>BRIMIND Chat</span>
          </div>
          <button 
            className="menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        
        {isMenuOpen && (
          <div className="mobile-menu-overlay" onClick={() => setIsMenuOpen(false)}>
            <div className="mobile-menu" ref={menuRef} onClick={(e) => e.stopPropagation()}>
              <div className="mobile-menu-header">
                <h3>Navigation</h3>
                <button onClick={() => setIsMenuOpen(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="mobile-menu-items">
                <button
                  className={`mobile-menu-item ${isActive('/') ? 'active' : ''}`}
                  onClick={() => handleNavClick('/')}
                >
                  <MessageSquare size={20} />
                  <span>Chat</span>
                </button>
                
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    setIsMenuOpen(false);
                    onShowHistory && onShowHistory();
                  }}
                >
                  <History size={20} />
                  <span>Toggle History</span>
                </button>
                
                <button
                  className="mobile-menu-item"
                  onClick={() => handleNavClick('/profile')}
                >
                  <User size={20} />
                  <span>Profile</span>
                </button>
                
                <button
                  className="mobile-menu-item logout-btn"
                  onClick={() => {
                    setIsMenuOpen(false);
                    logout();
                  }}
                >
                  <LogOut size={20} />
                  <span>Logout</span>
                </button>
                
                <div className="mobile-menu-info">
                  <p>Vibe Coding is available on desktop for the best development experience.</p>
                  <a 
                    href="/vibe-coding" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="desktop-link"
                  >
                    Open in Desktop
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    );
  }

  return (
    <nav className="desktop-nav">
      <div className="nav-container">
        <div className="nav-brand">
          <Link to="/" className="brand-link">
            <MessageSquare size={28} />
            <span>BRIMIND Chat</span>
          </Link>
        </div>
        
        <div className="nav-items">
          <Link 
            to="/" 
            className={`nav-item ${isActive('/') ? 'active' : ''}`}
          >
            <MessageSquare size={20} />
            <span>Chat</span>
          </Link>
          
          <Link 
            to="/vibe-coding" 
            className={`nav-item ${isActive('/vibe-coding') ? 'active' : ''}`}
          >
            <Code2 size={20} />
            <span>Vibe Coding</span>
          </Link>
          
          <div className="nav-stats-group">
            <div className="nav-stat-item">
              <Clock size={14} />
              <span className="stat-value">{(sessionStats.sessionTokensRemaining || 0).toLocaleString()}</span>
              <span className="stat-label">Session Left</span>
            </div>
            
            <div className="nav-stat-item">
              <Zap size={14} />
              <span className="stat-value">{(sessionStats.sessionTokensUsed || 0).toLocaleString()}</span>
              <span className="stat-label">Used</span>
            </div>
            
            <div className="nav-stat-item">
              <DollarSign size={14} />
              <span className="stat-value">${(sessionStats.balance || 0).toFixed(4)}</span>
              <span className="stat-label">Balance</span>
            </div>
          </div>
          
          <div 
            className="nav-item user-info clickable"
            onClick={() => navigate('/profile')}
            title="Profile"
          >
            <span style={{color: '#fff'}}>{user?.email?.length > 9 ? user.email.substring(0, 9) + '...' : user?.email}</span>
            <span className="balance">${(parseFloat(user?.balance) || 0).toFixed(4)}</span>
          </div>
          
          {/* <button 
            className="nav-item"
            onClick={logout}
            title="Logout"
          >
            <LogOut size={20} />
          </button> */}
          
          <button 
            className="nav-item"
            onClick={logout}
            title="Logout"
          >
            <LogOut size={20} />
          </button>
        </div>
      </div>
    </nav>
   
  );
};

export default Navigation;